import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/atomic/Card";
import { cn } from "@/lib/utils";
import { Problem } from "@/models/summary";
import { ActionSection } from "./ActionSection";
import { useProblemCardState } from "./hooks/useProblemCardState";
import { ProblemCardHeader } from "./ProblemCardHeader";
import { ProblemSummary } from "./ProblemSummary";
import { ToggleIndicator } from "./ToggleIndicator";

interface ProblemCardProps {
  problem: Problem;
  totalCaseCount: number;
  countermeasureDescription: string;
  openVocView: (problem: string) => void;
}

const ProblemCard: FC<ProblemCardProps> = ({
  problem,
  totalCaseCount,
  countermeasureDescription,
  openVocView,
}) => {
  const { t } = useTranslation("voiceek_ui");
  const {
    isClosed,
    setIsClosed,
    progressPercentageFloor,
    referenceText,
    isComparisionMode,
  } = useProblemCardState({ problem, totalCaseCount });

  const { caseCount } = problem;

  return (
    <Card
      className={cn(
        "flex flex-col w-full items-center gap-2.5 bg-[#fafbff] rounded-[10px] border border-solid border-color-border",
        isClosed || (isComparisionMode && (caseCount === 0)) ? "px-5 py-3" : "p-5",
        isComparisionMode && "min-h-[80px]",
      )}
    >
      <CardContent className="relative w-full p-0">
        <div className="relative w-full">
          <ProblemCardHeader
            problem={problem}
            totalCaseCount={totalCaseCount}
            progressPercentageFloor={progressPercentageFloor}
            isComparisionMode={isComparisionMode}
            isClosed={isClosed}
            setIsClosed={setIsClosed}
            t={t}
          />

          {!isClosed && caseCount > 0 && (
            <ProblemSummary
              referenceText={referenceText}
              openVocView={openVocView}
              problem={problem}
            />
          )}
        </div>

        {!isClosed && caseCount > 0 && (
          <ActionSection
            countermeasureDescription={countermeasureDescription}
            t={t}
          />
        )}
        {isComparisionMode && caseCount > 0 && <ToggleIndicator isClosed={isClosed} setIsClosed={setIsClosed} />}
      </CardContent>
    </Card>
  );
};

export default ProblemCard;
